import React, { useRef, useState } from 'react';

// Drag-select rectangle over the preview image (mouse + touch via Pointer Events), then
// crops that region into a new canvas on confirm. Coordinates are tracked in on-screen
// display pixels and converted to the image's natural pixel size only at crop time.
const ImageCropOverlay = ({ imgSrc, onConfirm, onCancel }) => {
    const imgRef = useRef(null);
    const containerRef = useRef(null);
    const [dragStart, setDragStart] = useState(null);
    const [rect, setRect] = useState(null); // { x, y, w, h } in display px, relative to container

    const getRelativePoint = (e) => {
        const bounds = containerRef.current.getBoundingClientRect();
        return {
            x: Math.min(Math.max(e.clientX - bounds.left, 0), bounds.width),
            y: Math.min(Math.max(e.clientY - bounds.top, 0), bounds.height),
        };
    };

    const onPointerDown = (e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        const p = getRelativePoint(e);
        setDragStart(p);
        setRect({ x: p.x, y: p.y, w: 0, h: 0 });
    };

    const onPointerMove = (e) => {
        if (!dragStart) return;
        const p = getRelativePoint(e);
        setRect({
            x: Math.min(dragStart.x, p.x),
            y: Math.min(dragStart.y, p.y),
            w: Math.abs(p.x - dragStart.x),
            h: Math.abs(p.y - dragStart.y),
        });
    };

    const onPointerUp = () => setDragStart(null);

    const hasSelection = rect && rect.w > 8 && rect.h > 8;

    const confirmCrop = () => {
        const img = imgRef.current;
        if (!img || !hasSelection) return;
        // img is `object-contain` inside a container whose aspect ratio rarely matches the
        // image's own — its element box (clientWidth/Height) spans the full container, but
        // the actually-painted pixels are letterboxed/pillarboxed within that box. Selection
        // coordinates are relative to the container, so they must be mapped through the same
        // "contain" fit math, not naively scaled against the element's full box.
        const cw = img.clientWidth;
        const ch = img.clientHeight;
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const fitScale = Math.min(cw / iw, ch / ih);
        const renderedW = iw * fitScale;
        const renderedH = ih * fitScale;
        const offsetX = (cw - renderedW) / 2;
        const offsetY = (ch - renderedH) / 2;
        const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
        const sx = clamp((rect.x - offsetX) / fitScale, 0, iw);
        const sy = clamp((rect.y - offsetY) / fitScale, 0, ih);
        const sw = clamp(rect.w / fitScale, 0, iw - sx);
        const sh = clamp(rect.h / fitScale, 0, ih - sy);
        if (sw < 1 || sh < 1) return;
        const canvas = document.createElement('canvas');
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        onConfirm(canvas.toDataURL('image/jpeg', 0.9));
    };

    return (
        <div>
            <div
                ref={containerRef}
                className="relative mx-auto h-72 w-full rounded-xl border border-white/10 overflow-hidden touch-none select-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
            >
                <img ref={imgRef} src={imgSrc} alt="Crop source" className="w-full h-full object-contain pointer-events-none" draggable={false} />
                {rect && (
                    <div
                        className="absolute border-2 border-teal-400 bg-teal-400/15"
                        style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
                    />
                )}
            </div>
            <p className="mt-2 text-xs text-slate-500 text-center">Drag over the part of the image you want cards focused on</p>
            <div className="mt-3 flex items-center justify-center gap-3">
                <button type="button" onClick={onCancel} className="rounded-xl px-4 py-2 text-sm text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition">
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={confirmCrop}
                    disabled={!hasSelection}
                    className="rounded-xl px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-40 transition"
                >
                    Use this region
                </button>
            </div>
        </div>
    );
};

/** Reuses FlashFoto image upload / camera patterns with existing app theme. */
const ClinicalImageCapture = ({
    imagePreview,
    documentFileName,
    dragActive,
    isGenerating,
    isCameraOn,
    cameraErr,
    fileInputRef,
    videoRef,
    canvasRef,
    onDrop,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onFileChange,
    openFilePicker,
    startCamera,
    stopCamera,
    takePicture,
    statusMsg,
    onImageCropReady,
    onSelectionError,
}) => {
    const [isCropping, setIsCropping] = useState(false);

    return (
    <div>
        <div
            className={`relative border-2 border-dashed rounded-2xl ${dragActive ? 'border-teal-600/50' : 'border-slate-700'} bg-slate-950/40`}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {!isCropping && (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={onFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
            )}
            <div className="p-6 sm:p-8 text-center">
                {isGenerating && !imagePreview && !documentFileName ? (
                    <div className="animate-pulse h-40 w-full rounded-xl bg-slate-800/50 border border-white/10" />
                ) : imagePreview && isCropping ? (
                    <div className="relative z-10">
                        <ImageCropOverlay
                            imgSrc={imagePreview}
                            onCancel={() => setIsCropping(false)}
                            onConfirm={(croppedDataUrl) => { setIsCropping(false); onImageCropReady?.(croppedDataUrl); }}
                        />
                    </div>
                ) : imagePreview ? (
                    <div>
                        <img src={imagePreview} alt="Clinical content preview" className="mx-auto max-h-52 w-full object-contain rounded-xl border border-white/10" />
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setIsCropping(true); }}
                            className="relative z-10 mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-teal-300 bg-teal-500/10 hover:bg-teal-500/20 ring-1 ring-teal-500/20 transition"
                        >
                            ✂️ Select a specific region
                        </button>
                    </div>
                ) : documentFileName ? (
                    <div className="flex items-center justify-center gap-3 rounded-xl border border-white/10 bg-slate-800/40 py-6 px-4">
                        <svg viewBox="0 0 24 24" width="28" height="28" className="text-teal-300 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span className="text-sm text-slate-200 truncate">{documentFileName}</span>
                    </div>
                ) : (
                    <>
                        <div className="mx-auto h-12 w-12 rounded-xl bg-slate-800/70 border border-white/10 flex items-center justify-center">
                            <svg viewBox="0 0 24 24" width="20" height="20" className="text-slate-300" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                            </svg>
                        </div>
                        <p className="mt-4 text-sm text-slate-400">Upload slides, textbook pages, clinical images, or a PDF/Word/PowerPoint/Excel file</p>
                    </>
                )}
            </div>
        </div>

        {isCameraOn && (
            <div className="mt-4 space-y-3">
                <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-xl border border-white/10" />
                {/* Centered and wide so the primary action isn't biased toward either hand;
                    Stop stacked underneath as the clearly secondary action. */}
                <div className="flex flex-col items-center gap-2">
                    <button
                        type="button"
                        onClick={takePicture}
                        className="w-full max-w-sm inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base text-white font-semibold bg-gradient-to-r from-teal-600 to-emerald-600 shadow-lg shadow-teal-500/20"
                    >
                        📸 Snap
                    </button>
                    <button type="button" onClick={stopCamera} className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2 text-sm text-slate-200 bg-slate-800/70 border border-white/10">
                        Stop
                    </button>
                </div>
                <canvas ref={canvasRef} className="hidden" />
            </div>
        )}
        {cameraErr && <p className="text-xs text-rose-300 mt-2">{cameraErr}</p>}

        {!isCameraOn && !isCropping && (
            <div className="mt-4 flex flex-wrap justify-center gap-3">
                <button type="button" onClick={openFilePicker} disabled={isGenerating} className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-white font-medium bg-gradient-to-r from-teal-600 to-emerald-600 disabled:opacity-60">
                    Upload image
                </button>
                <button type="button" onClick={startCamera} className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-slate-200 bg-slate-800/70 ring-1 ring-white/10">
                    Use camera
                </button>
            </div>
        )}
        {statusMsg && <p className="mt-3 text-xs text-slate-400">{statusMsg}</p>}
    </div>
    );
};

export default ClinicalImageCapture;
